/**
 * MobiLoud Canvas WebView detection utilities.
 *
 * Shared across upload_video.js, uploader.js, and any other module that
 * needs to detect the MobiLoud Canvas wrapper on iOS or Android.
 *
 * Re-exports isMobiLoudEnvironment from the canonical push_registration
 * source so callers don't need to import from the push module directly.
 */
import { isMobiLoudEnvironment } from "../mobiloud/push_registration";
import { isCapacitorEnvironment } from "../capacitor/detection";

/**
 * Detect any native app shell, regardless of wrapper. True inside either the
 * legacy MobiLoud Canvas wrapper or the self-owned Capacitor shell that is
 * replacing it. Use this (not isMobiLoudEnvironment) for behavior that should
 * apply to the native app in general, so it keeps working through the cutover
 * and after MobiLoud is retired.
 * @returns {boolean}
 */
export const isNativeApp = () =>
  isMobiLoudEnvironment() || isCapacitorEnvironment();

/**
 * Detect Android MobiLoud Canvas WebView specifically.
 * Returns true when the user agent contains both "canvas" (MobiLoud)
 * and "Android".
 */
export const isAndroidCanvasWebView = () => {
  if (typeof navigator === "undefined") {
    return false;
  }
  return isMobiLoudEnvironment() && /Android/i.test(navigator.userAgent || "");
};

/**
 * Detect either Android app: the MobiLoud Canvas wrapper or the Capacitor
 * shell that replaced it as an in-place update of the same Play Store app
 * (2026-10-06).
 *
 * Both wrap the Android System WebView, so a workaround for how that WebView
 * reads picked files belongs here, not on isAndroidCanvasWebView. The
 * Capacitor user agent has no "canvas", and every Android file-read
 * workaround keyed on isAndroidCanvasWebView stopped applying to Android app
 * users the day the update landed. Keep isAndroidCanvasWebView for behavior
 * that really is MobiLoud's (its push bridge, its single-use file handles).
 * @returns {boolean}
 */
export const isAndroidAppWebView = () => {
  if (typeof navigator === "undefined") {
    return false;
  }
  if (!/Android/i.test(navigator.userAgent || "")) {
    return false;
  }
  return isMobiLoudEnvironment() || isCapacitorEnvironment();
};

/**
 * Detect any mobile Android browser, not just the MobiLoud Canvas WebView.
 * True for Chrome / Samsung Internet / Firefox / etc. on Android phones and
 * tablets in addition to the Canvas WebView shell.
 *
 * Used by inferMediaTypeAsync's assumeCategory fallback (issue #3412): Samsung
 * clients on non-Canvas shells hit the same garbage-MIME + unknown-extension
 * file path that PR #2680 already addressed for Canvas users.
 */
export const isMobileAndroid = () => {
  if (typeof navigator === "undefined") {
    return false;
  }
  return /Android/i.test(navigator.userAgent || "");
};

export { isMobiLoudEnvironment, isCapacitorEnvironment };
